import { Check, Palette } from '@phosphor-icons/react'
import { Editor } from '@tiptap/react'
import { Popover } from 'antd'
import { useState } from 'react'

import cn from '@/utils/tailwind/cn'

type Props = {
  editor: Editor
}

export const TextEditorToolbarTextColor = ({ editor }: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const closePopover = () => setIsOpen(false)

  const highlightColors = [
    { id: 'black', color: '#000000' },
    { id: 'dark-gray-4', color: '#434343' },
    { id: 'dark-gray-3', color: '#666666' },
    { id: 'dark-gray-2', color: '#999999' },
    { id: 'dark-gray-1', color: '#b7b7b7' },
    { id: 'gray', color: '#cccccc' },
    { id: 'light-gray-1', color: '#d9d9d9' },
    { id: 'light-gray-2', color: '#efefef' },
    { id: 'light-gray-3', color: '#f3f3f3' },
    { id: 'white', color: '#ffffff' },

    { id: 'red-berry', color: '#980000' },
    { id: 'red', color: '#ff0000' },
    { id: 'orange', color: '#ff9900' },
    { id: 'yellow', color: '#ffff00' },
    { id: 'green', color: '#00ff00' },
    { id: 'cyan', color: '#00ffff' },
    { id: 'cornflower-blue', color: '#4a86e8' },
    { id: 'blue', color: '#0000ff' },
    { id: 'purple', color: '#9900ff' },
    { id: 'magenta', color: '#ff00ff' },

    { id: 'light-red-berry-3', color: '#e6b8af' },
    { id: 'light-red-3', color: '#f4cccc' },
    { id: 'light-orange-3', color: '#fce5cd' },
    { id: 'light-yellow-3', color: '#fff2cc' },
    { id: 'light-green-3', color: '#d9ead3' },
    { id: 'light-cyan-3', color: '#d0e0e3' },
    { id: 'light-cornflower-blue-3', color: '#c9daf8' },
    { id: 'light-blue-3', color: '#cfe2f3' },
    { id: 'light-purple-3', color: '#d9d2e9' },
    { id: 'light-magenta-3', color: '#ead1dc' },

    { id: 'light-red-berry-2', color: '#dd7e6b' },
    { id: 'light-red-2', color: '#ea9999' },
    { id: 'light-orange-2', color: '#f9cb9c' },
    { id: 'light-yellow-2', color: '#ffe599' },
    { id: 'light-green-2', color: '#b6d7a8' },
    { id: 'light-cyan-2', color: '#a2c4c9' },
    { id: 'light-cornflower-blue-2', color: '#a4c2f4' },
    { id: 'light-blue-2', color: '#9fc5e8' },
    { id: 'light-purple-2', color: '#b4a7d6' },
    { id: 'light-magenta-2', color: '#d5a6bd' },

    { id: 'light-red-berry-1', color: '#cc4125' },
    { id: 'light-red-1', color: '#e06666' },
    { id: 'light-orange-1', color: '#f6b26b' },
    { id: 'light-yellow-1', color: '#ffd966' },
    { id: 'light-green-1', color: '#93c47d' },
    { id: 'light-cyan-1', color: '#76a5af' },
    { id: 'light-cornflower-blue-1', color: '#6d9eeb' },
    { id: 'light-blue-1', color: '#6fa8dc' },
    { id: 'light-purple-1', color: '#8e7cc3' },
    { id: 'light-magenta-1', color: '#c27ba0' },

    { id: 'dark-red-berry-1', color: '#a61c00' },
    { id: 'dark-red-1', color: '#cc0000' },
    { id: 'dark-orange-1', color: '#e69138' },
    { id: 'dark-yellow-1', color: '#f1c232' },
    { id: 'dark-green-1', color: '#6aa84f' },
    { id: 'dark-cyan-1', color: '#45818e' },
    { id: 'dark-cornflower-blue-1', color: '#3c78d8' },
    { id: 'dark-blue-1', color: '#3d85c6' },
    { id: 'dark-purple-1', color: '#674ea7' },
    { id: 'dark-magenta-1', color: '#a64d79' },

    { id: 'dark-red-berry-2', color: '#85200c' },
    { id: 'dark-red-2', color: '#990000' },
    { id: 'dark-orange-2', color: '#b45f06' },
    { id: 'dark-yellow-2', color: '#bf9000' },
    { id: 'dark-green-2', color: '#38761d' },
    { id: 'dark-cyan-2', color: '#134f5c' },
    { id: 'dark-cornflower-blue-2', color: '#1155cc' },
    { id: 'dark-blue-2', color: '#0b5394' },
    { id: 'dark-purple-2', color: '#351c75' },
    { id: 'dark-magenta-2', color: '#741b47' },

    { id: 'dark-red-berry-3', color: '#5b0f00' },
    { id: 'dark-red-3', color: '#660000' },
    { id: 'dark-orange-3', color: '#783f04' },
    { id: 'dark-yellow-3', color: '#7f6000' },
    { id: 'dark-green-3', color: '#274e13' },
    { id: 'dark-cyan-3', color: '#0c343d' },
    { id: 'dark-cornflower-blue-3', color: '#1c4587' },
    { id: 'dark-blue-3', color: '#073763' },
    { id: 'dark-purple-3', color: '#20124d' },
    { id: 'dark-magenta-3', color: '#4c1130' },
  ]

  return (
    <Popover
      classNames={{ body: '!px-0' }}
      content={
        <div className="grid w-full grid-cols-10 grid-rows-8 items-center justify-center gap-0.5 px-2.5">
          {highlightColors.map((item) => {
            const isActive = editor.isActive('textStyle', { color: item.color })
            return (
              <button
                className={cn(
                  'flex h-[21px] w-[21px] items-center justify-start rounded-full border border-gray-100/10 outline-none hover:opacity-70 focus:outline-none disabled:cursor-not-allowed',
                  isActive && 'shadow'
                )}
                key={item.id}
                onClick={() => {
                  editor.chain().focus().setColor(item.color).run()
                  closePopover()
                }}
                style={{ background: item.color }}
                type="button"
              >
                {isActive ? (
                  <Check
                    className="mb-0.5 ml-0.5 text-sm text-white"
                    weight="bold"
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      }
      getPopupContainer={() => document.body}
      onOpenChange={() => setIsOpen((old) => !old)}
      open={isOpen}
      placement="bottom"
      trigger="click"
    >
      <button
        className="flex h-6 flex-col items-center justify-center gap-1 rounded-sm border-2 border-transparent bg-transparent p-0 text-xl text-gray-700 outline-none hover:text-primary focus:outline-none disabled:cursor-not-allowed"
        type="button"
      >
        <Palette weight="bold" />
      </button>
    </Popover>
  )
}
